import { v4 as uuid } from 'uuid'
import { Base64 } from 'js-base64';

let moveObjectInArray = (array, objectUuid, newPosition) => {
    let object = array.find(object => object.uuid === objectUuid)

    let oldWeight = array.indexOf(object) + 1
    let newWeight = newPosition + 1

    object.weight = newWeight

    if (oldWeight > newWeight) {
        array.filter(object => object.uuid !== objectUuid && object.weight >= newWeight && object.weight <= oldWeight).forEach(object => object.weight++)
    } else if (oldWeight < newWeight) {
        array.filter(object => object.uuid !== objectUuid && object.weight >= oldWeight && object.weight <= newWeight).forEach(object => object.weight--)
    }

    return array.sort((objectA, objectB) => objectA.weight - objectB.weight)
}

export default ({ page, blocks, translations }) => ({
    /**
     * State
     */
    page: page,
    blocks: blocks,

    translations: translations,

    activeButton: 'settings:general',
    selectedContentItemUuid: null,

    __cachedThemes: null,
    __cachedSectionsPerTheme: [],
    __cachedPages: null,
    __cachedLegislations: [],
    __cachedProcedures: [],
    __cachedPoliceZones: null,
    __cachedJudicialDistricts: null,

    originalPageEncodedString: null,
    originalBlocksEncodedString: null,

    livewireNavigateUrl: null,
    hasConfirmedLivewireNavigate: false,

    /**
     * Initialization
     */
    init() {
        let initialize = (afterSaving = false) => {
            this.blocks.forEach((block, index) => {
                block.weight = index + 1

                block.content.forEach((contentItem, index) => {
                    contentItem.weight = index + 1
                })
            })

            this.originalPageEncodedString = Base64.encode(JSON.stringify(this.page))
            this.originalBlocksEncodedString = Base64.encode(JSON.stringify(this.blocks))

            let visibleBlocks = this.blocks.filter(block => !block.hidden);

            if (this.activeButton.startsWith('block:') && visibleBlocks.find(block => `block:${block.uuid}` === this.activeButton)) {
                return
            }

            if (!this.activeButton.startsWith('block:') && afterSaving) {
                return
            }

            if (visibleBlocks.length > 0) {
                this.activeButton = `block:${this.blocks.filter(block => !block.hidden)[0].uuid}`
            }
        }

        this.$watch('activeButton', () => this.selectedContentItemUuid = null)

        this.$wire.on('saved', () => this.$nextTick(() => initialize(true)))

        this.$nextTick(() => {
            initialize()

            let warnBeforeLeavingViaLivewireNavigate = (event) => {
                if (this.hasConfirmedLivewireNavigate) {
                    this.livewireNavigateUrl = null
                    this.hasConfirmedLivewireNavigate = false
                    this.$flux.modal('cancel').close()

                    return
                }

                if (!this.hasUnsavedChanges) {
                    return
                }

                event.preventDefault()

                this.livewireNavigateUrl = event.detail.url
                this.$flux.modal('cancel').show()
            };

            document.addEventListener('livewire:navigate', warnBeforeLeavingViaLivewireNavigate)
            document.addEventListener('livewire:navigated', () => document.removeEventListener('livewire:navigate', warnBeforeLeavingViaLivewireNavigate))

            window.addEventListener('beforeunload', this.warnBeforeLeavingViaBrowser)
        })
    },

    destroy() {
        window.removeEventListener('beforeunload', this.warnBeforeLeavingViaBrowser)
    },

    get valid() {
        return !!this.page.title && this.blocks.filter(block => this.isInvalidBlock(block)).length === 0
    },

    get hasUnsavedChanges() {
        return this.originalPageEncodedString !== Base64.encode(JSON.stringify(this.page))
            || this.originalBlocksEncodedString !== Base64.encode(JSON.stringify(this.blocks))
    },

    get activeBlock() {
        if (!this.activeButton || !this.activeButton.startsWith('block:')) {
            return null
        }

        return this.blocks.find(block => `block:${block.uuid}` === this.activeButton)
    },
    get selectedContentItem() {
        if (!this.activeBlock || !this.selectedContentItemUuid) {
            return null
        }

        return this.activeBlock.content.find(item => item.uuid === this.selectedContentItemUuid)
    },

    warnBeforeLeavingViaBrowser(event) {
        event.preventDefault();
        event.returnValue = '';
    },

    isInvalidBlock(block) {
        return !block.title
    },

    addNewOwnerlessBlock() {
        let newBlock = this.__addNewBlock({
            title: this.translations.new_ownerless_block_title,
            police_zone_id: null,
            judicial_district_id: null,
        })

        this.addNewTextContentItem(newBlock)
    },
    addNewPoliceZoneBlock(policeZoneId = null) {
        this.getPoliceZones().then(policeZones => {
            let policeZone = policeZoneId ? policeZones.find(policeZone => policeZone.id == policeZoneId) : policeZones[0]

            let newBlock = this.__addNewBlock({
                title: this.translations.new_police_zone_block_title,
                subtitle: policeZone.name,
                police_zone_id: policeZone.id,
                judicial_district_id: null,
                owner_readonly: policeZoneId !== null,
                open_by_default: true,
            })

            this.addNewTextContentItem(newBlock)
        })
    },
    addNewJudicialDistrictBlock(judicialDistrictId = null) {
        this.getJudicialDistricts().then(judicialDistricts => {
            let judicialDistrict = judicialDistrictId ? judicialDistricts.find(judicialDistrict => judicialDistrict.id == judicialDistrictId) : judicialDistricts[0]

            let newBlock = this.__addNewBlock({
                title: this.translations.new_judicial_district_block_title,
                subtitle: judicialDistrict.name,
                police_zone_id: null,
                judicial_district_id: judicialDistrict.id,
                owner_readonly: judicialDistrictId !== null,
            })

            this.addNewTextContentItem(newBlock)
        })
    },
    __addNewBlock(newBlock) {
        newBlock.uuid = uuid()
        newBlock.open_by_default ??= false
        newBlock.content ??= []
        newBlock.weight = this.blocks.length + 1
        newBlock.hidden ??= false
        newBlock.readonly ??= false
        newBlock.owner_readonly ??= false

        this.blocks.push(newBlock)

        this.activeButton = `block:${newBlock.uuid}`

        return newBlock
    },

    deleteBlock(blockUuid) {
        this.activeButton = `settings:general`

        queueMicrotask(() => {
            this.blocks = this.blocks.filter(block => block.uuid !== blockUuid)
            this.blocks.forEach((block, index) => block.weight = index + 1)
        })
    },

    moveBlock(blockUuid, newPosition) {
        this.blocks = moveObjectInArray(this.blocks, blockUuid, newPosition)
    },

    moveUpContentItem(block, contentItemUuid) {
        let newPosition = block.content.find(contentItem => contentItem.uuid === contentItemUuid).weight - 2

        if (newPosition < 0) {
            return
        }

        block.content = moveObjectInArray(block.content, contentItemUuid, newPosition)
    },
    moveDownContentItem(block, contentItemUuid) {
        let newPosition = block.content.find(contentItem => contentItem.uuid === contentItemUuid).weight

        if (newPosition >= block.content.length) {
            return
        }

        block.content = moveObjectInArray(block.content, contentItemUuid, newPosition)
    },

    addNewTextContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'text',
            data: {
                html: null,
            },
        })
    },

    addNewTableContentItem(block) {
        let contentItem = this.__addNewContentItem(block, {
            type: 'table',
            data: {
                rows: [],
                column_widths: [50, 50],
                caption: null,
            },
        })

        this.addNewRowToTable(contentItem)
    },
    addNewRowToTable(contentItem) {
        contentItem.data.rows.push({uuid: uuid(), cells: [{html: null}, {html: null}]})
    },

    addNewImageContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'image',
            data: {
                url: null,
                caption: null,
            },
        })
    },

    addNewWebsiteButtonContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'button',
            data: {
                label: this.translations.new_website_button_content_item_label,
                url: 'https://',
            },
        })
    },
    addNewEmailButtonContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'button',
            data: {
                label: this.translations.new_email_button_content_item_label,
                url: 'mailto:',
            },
        })
    },
    addNewPageButtonContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'button',
            data: {
                label: this.translations.new_page_button_content_item_label,
                url: 'page://',
            },
        })
    },
    addNewLegislationButtonContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'button',
            data: {
                label: this.translations.new_legislation_button_content_item_label,
                url: 'legislation://',
            },
        })
    },
    addNewProcedureButtonContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'button',
            data: {
                label: this.translations.new_procedure_button_content_item_label,
                url: 'procedure://',
            },
        })
    },

    addNewWebsiteLinkContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'link',
            data: {
                label: this.translations.new_website_link_content_item_label,
                url: 'https://',
            },
        })
    },
    addNewEmailLinkContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'link',
            data: {
                label: this.translations.new_email_link_content_item_label,
                url: 'mailto:',
            },
        })
    },
    addNewPageLinkContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'link',
            data: {
                label: this.translations.new_page_link_content_item_label,
                url: 'page://',
            },
        })
    },
    addNewLegislationLinkContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'link',
            data: {
                label: this.translations.new_legislation_link_content_item_label,
                url: 'legislation://',
            },
        })
    },
    addNewProcedureLinkContentItem(block) {
        this.__addNewContentItem(block, {
            type: 'link',
            data: {
                label: this.translations.new_procedure_link_content_item_label,
                url: 'procedure://',
            },
        })
    },

    __addNewContentItem(block, item) {
        item.uuid = uuid()
        item.weight = block.content.length + 1

        block.content.push(item)

        this.$nextTick(() => {
            let el = document.querySelector(`[data-content-items]`)

            el.scrollTop = el.scrollHeight

            if (['button', 'link', 'table', 'image'].includes(item.type)) {
                this.selectedContentItemUuid = item.uuid
            }
        })

        return item
    },

    getThemes() {
        return new Promise((resolve, reject) => {
            if (this.__cachedThemes !== null) {
                return resolve(this.__cachedThemes)
            }

            fetch('/internal-api/themes', { headers: {'accept': 'application/json'} })
                .then(response => {
                    if (response.status === 200) {
                        return response.json().then(json => {
                            this.__cachedThemes = json

                            resolve(this.__cachedThemes)
                        })
                    } else if (response.status === 401) {
                        window.location = '/login'
                    } else {
                        reject(response)
                    }
                })
                .catch(reject)
        })
    },

    getSectionsOfTheme(themeId) {
        return new Promise((resolve, reject) => {
            let cachedSections = this.__cachedSectionsPerTheme.find(theme => theme.id == themeId)?.sections

            if (cachedSections !== undefined) {
                return resolve(cachedSections)
            }

            fetch(`/internal-api/theme/${themeId}/sections`, { headers: {'accept': 'application/json'} })
                .then(response => {
                    if (response.status === 200) {
                        return response.json().then(json => {
                            this.__cachedSectionsPerTheme.push({id: themeId, sections: json})

                            resolve(json)
                        })
                    } else if (response.status === 401) {
                        window.location = '/login'
                    } else {
                        reject(response)
                    }
                })
                .catch(reject)
        })
    },

    getPages() {
        return new Promise((resolve, reject) => {
            if (this.__cachedPages !== null) {
                return resolve(this.__cachedPages)
            }

            fetch('/internal-api/pages', { headers: {'accept': 'application/json'} })
                .then(response => {
                    if (response.status === 200) {
                        return response.json().then(json => {
                            json.forEach(page => page.title = page.title.trim())

                            this.__cachedPages = json

                            resolve(this.__cachedPages)
                        })
                    } else if (response.status === 401) {
                        window.location = '/login'
                    } else {
                        reject(response)
                    }
                })
                .catch(reject)
        })
    },
    getPageById(pageId) {
        return new Promise((resolve, reject) => {
            this.getPages()
                .then(pages => resolve(pages.find(page => page.id == pageId)))
                .catch(reject)
        })
    },

    getLegislationById(legislationId) {
        return new Promise((resolve, reject) => {
            let cachedLegislation = this.__cachedLegislations.find(legislation => legislation.id == legislationId)

            if (cachedLegislation !== undefined) {
                return resolve(cachedLegislation)
            }

            fetch(`/internal-api/legislation/${legislationId}`, { headers: {'accept': 'application/json'} })
                .then(response => {
                    if (response.status === 200) {
                        return response.json().then(json => {
                            this.__cachedLegislations.push(json)

                            resolve(json)
                        })
                    } else if (response.status === 401) {
                        window.location = '/login'
                    } else {
                        reject(response)
                    }
                })
                .catch(reject)
        })
    },

    getProcedureById(procedureId) {
        return new Promise((resolve, reject) => {
            let cachedProcedure = this.__cachedProcedures.find(procedure => procedure.id == procedureId)

            if (cachedProcedure !== undefined) {
                return resolve(cachedProcedure)
            }

            fetch(`/internal-api/procedure/${procedureId}`, { headers: {'accept': 'application/json'} })
                .then(response => {
                    if (response.status === 200) {
                        return response.json().then(json => {
                            this.__cachedProcedures.push(json)

                            resolve(json)
                        })
                    } else if (response.status === 401) {
                        window.location = '/login'
                    } else {
                        reject(response)
                    }
                })
                .catch(reject)
        })
    },

    getPoliceZones() {
        return new Promise((resolve, reject) => {
            if (this.__cachedPoliceZones !== null) {
                return resolve(this.__cachedPoliceZones)
            }

            fetch('/internal-api/police-zones', { headers: {'accept': 'application/json'} })
                .then(response => {
                    if (response.status === 200) {
                        return response.json().then(json => {
                            json.forEach(policeZone => policeZone.name = policeZone.name.trim())

                            this.__cachedPoliceZones = json

                            resolve(this.__cachedPoliceZones)
                        })
                    } else if (response.status === 401) {
                        window.location = '/login'
                    } else {
                        reject(response)
                    }
                })
                .catch(reject)
        })
    },

    getJudicialDistricts() {
        return new Promise((resolve, reject) => {
            if (this.__cachedJudicialDistricts !== null) {
                return resolve(this.__cachedJudicialDistricts)
            }

            fetch('/internal-api/judicial-districts', { headers: {'accept': 'application/json'} })
                .then(response => {
                    if (response.status === 200) {
                        return response.json().then(json => {
                            json.forEach(judicialDistrict => judicialDistrict.name = judicialDistrict.name.trim())

                            this.__cachedJudicialDistricts = json

                            resolve(this.__cachedJudicialDistricts)
                        })
                    } else if (response.status === 401) {
                        window.location = '/login'
                    } else {
                        reject(response)
                    }
                })
                .catch(reject)
        })
    },

    openUrlInBlankTab(url) {
        if (url.startsWith('page://')) {
            window.open(`/page/${url.replace(/^page:\/\//, '' )}/edit`, '_blank')
        } else if (url.startsWith('legislation://')) {
            this.getLegislationById(url.replace(/^legislation:\/\//, '' )).then(legislation => {
                window.open(legislation.vandenbroele_url, '_blank')
            })
        } else if (url.startsWith('procedure://')) {
            this.getProcedureById(url.replace(/^procedure:\/\//, '' )).then(procedure => {
                window.open(procedure.vandenbroele_url, '_blank')
            })
        } else {
            window.open(url, '_blank')
        }
    },

    getPreviousUrl() {
        return new URL(document.location.toString()).searchParams.get('previous_url')
    }
})
